// components/footfall/merchChat.ts
import type { useKernel } from '@/kernel/Kernel';
import { unitName } from '#lib/footfall/pulse';
import { campaignLabel } from '#lib/leasing/campaignCard';
import type { MerchGreetingRequest } from '#lib/footfall/merchGreeting';
import type { CampaignDTO } from '#types/types';
import { derivePulseFromState } from './pulseDerive';
import { patchPulse, savePlan } from './pulseState';
import { analystDocId } from '#lib/leasing/analystWorkbook';
import { businessPlanSheet, withBusinessPlan } from '#lib/footfall/scenario';
import { valueAsset } from '#lib/analyst/valuation';
import { publishedItem } from '#lib/analyst/assetScenarios';

type Kernel = NonNullable<ReturnType<typeof useKernel>['kernel']['current']>;

/** The facts the plan on screen already shows about its centre: the greeting may say nothing the user could not check. */
export function merchGreetingFacts(k: Kernel): MerchGreetingRequest['facts'] | null {
  const state = k.getState();
  const w = derivePulseFromState(state);
  if (!w.property) return null;
  const run = state.pulse.draft?.campaignId ? state.inventory.Campaign.registry[state.pulse.draft.campaignId] as CampaignDTO | undefined : undefined;
  const nameOf = (shopId: string) => { const f = w.places.find(p => p.feature.id === shopId)?.feature; return f ? unitName(f) : shopId; };
  const ready = w.shortlistStatus === 'ready';
  return {
    centre: w.property.name,
    positioning: ((w.property as { positioningNote?: string | null }).positioningNote ?? '').slice(0, 600) || undefined,
    occupancy: w.totals.occupancyToday,
    vacancies: w.todayPlaces.filter(p => p.category === 'VACANT').length,
    run: run ? campaignLabel(run, 'Source run') : undefined,
    shortlist: ready ? w.shortlist.slice(0, 5).map(b => ({ name: b.name, fit: b.fitScore ?? null, size: b.surfaceMin || b.surfaceMax ? `${b.surfaceMin ?? '?'}-${b.surfaceMax ?? '?'} m²` : undefined })) : undefined,
    opportunities: w.opps.slice(0, 3).map(o => ({ unit: nameOf(o.shopId), kind: o.kind, evidence: o.evidence.slice(0, 240), value: o.value, brands: o.brands.slice(0, 3).map(b => b.name) })),
    scenarios: w.scenarios.slice(0, 3).map(s => ({ name: s.name, moves: s.moves.length, valueCreated: ready && w.scenario.yieldPct ? s.totals.valueCreated : null, onMap: s.active })),
  };
}

/** Opens the Merch chat beside the plan, then asks the fast agent for one message about this centre. */
export async function openMerchChat(k: Kernel) {
  patchPulse(k, { chatOpen: true });
  const state = k.getState();
  const sessionId = state.activeChatSessionId;
  const propertyId = state.pulse.propertyId;
  k.logUserAction('Merch Chat Opened', { planId: state.pulse.planId, propertyId });
  const facts = merchGreetingFacts(k);
  if (!sessionId || !propertyId || !facts) return;
  try {
    const body: MerchGreetingRequest = { sessionId, propertyId, language: state.language === 'fr' ? 'fr' : 'en', facts };
    await fetch('/api/plan/greeting', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    // The greeting is a courtesy: the chat is open and works without it.
  }
}

/**
 * Marks Interesting, in the scenario's Source run, every brand the scenario places: the asset manager's pick goes
 * back to the shortlist. Only on the user's click, since the app cannot undo a mark (2.5). Returns how many it marked.
 */
export async function markScenarioBrandsInteresting(k: Kernel): Promise<number> {
  const state = k.getState();
  const w = derivePulseFromState(state);
  const placed = new Set(w.scenario.moves.flatMap(m => m.type === 'relet' && m.brandId ? [m.brandId] : []));
  const ids = state.pulse.shortlist?.status === 'ready' ? state.pulse.shortlist.ids : [];
  const leads = ids.map(id => state.inventory.Lead.registry[id]).filter((l): l is NonNullable<typeof l> => !!l && placed.has(l.brandId) && l.status !== 'INTERESTING');
  for (const lead of leads) await k.flagLeadInteresting(lead.id);
  k.logUserAction('Merch Scenario Brands Marked Interesting', { planId: state.pulse.planId, count: leads.length });
  return leads.length;
}

/**
 * Writes the scenario on the map into the asset's shared workbook as its Business Plan sheet (live formulas: the moves,
 * value created, the asset's rent over ten years), then opens it in Analyst. Owners only, as every workbook write.
 * Returns an error message, or null once Analyst is open on it.
 */
export async function openScenarioInAnalyst(k: Kernel): Promise<string | null> {
  const state = k.getState();
  const fr = state.language === 'fr';
  const w = derivePulseFromState(state);
  if (!w.property) return fr ? 'Aucun centre ouvert.' : 'No centre is open.';
  const { loadSharedTable, saveSharedTable } = await import('@/components/tables/tablesShared');
  const { useTablesStore } = await import('@/components/tables/tablesStore');
  const docId = analystDocId(w.property.id);
  if (!useTablesStore.getState().docs[docId]) {
    try { await loadSharedTable(k, w.property.id); } catch (error) { return error instanceof Error ? error.message : (fr ? 'Le classeur partagé n’a pas pu être chargé.' : 'The shared workbook could not be loaded.'); }
  }
  const doc = useTablesStore.getState().docs[docId];
  if (!doc) return fr ? 'Le classeur partagé de l’actif n’a pas pu être chargé.' : 'The asset’s shared workbook could not be loaded.';
  if (doc.sharedAccess !== 'owner') return fr ? 'Seuls les gestionnaires désignés de l’actif écrivent dans son classeur.' : 'Only the asset’s assigned owners write to its workbook.';
  const a = w.property.analystAssumptions;
  const income = w.property.rentRoll && a ? valueAsset(w.property.rentRoll, a).income : null;
  const sheet = businessPlanSheet(w.property.id, w.scenario, w.priced, w.levelName, { income, indexationPct: a?.indexationPct ?? null, mockup: !!a?.mockup || w.totals.estimatedRents > 0 });
  const sheets = withBusinessPlan(doc, sheet, false);
  if (!sheets) return fr ? 'Une feuille « Business Plan » écrite à la main existe déjà : renommez-la d’abord.' : 'A hand-made "Business Plan" sheet already exists: rename it first.';
  useTablesStore.setState(s => ({ docs: { ...s.docs, [docId]: { ...doc, sheets } } }));
  try { await saveSharedTable(k, docId); } catch (error) { return error instanceof Error ? error.message : (fr ? 'Le classeur n’a pas pu être enregistré.' : 'The workbook could not be saved.'); }
  // The plan is saved, then Analyst opens through its own left-bar button, as any tool switch does (the Daimon's open_module too).
  if (state.pulse.editVersion !== state.pulse.savedVersion) await savePlan(k);
  document.getElementById('portal-nav-tables')?.click();
  await new Promise(resolve => setTimeout(resolve, 0));
  useTablesStore.getState().openDoc(docId);
  const { navigateToAnalystCell } = await import('@/components/tables/tablesStore');
  navigateToAnalystCell(sheet.name, 'A1');
  k.logUserAction('Merch Scenario Opened In Analyst', { propertyId: w.property.id, planId: state.pulse.planId });
  return null;
}

/**
 * Publishes the scenario on the map to the asset (spec step 4): its managers see it, with its author and the totals it
 * was priced at, in Merch and in Analyst. Republishing the same scenario replaces it. Owners only, revisioned.
 */
export async function publishScenarioToAsset(k: Kernel): Promise<string | null> {
  const state = k.getState();
  const fr = state.language === 'fr';
  const w = derivePulseFromState(state);
  const draft = state.pulse.draft;
  if (!w.property || !draft) return fr ? 'Aucun plan ouvert.' : 'No plan is open.';
  if (state.pulse.editVersion !== state.pulse.savedVersion) await savePlan(k);
  const property = await k.syncEntity('Property', w.property.id, { force: true, requireFresh: true });
  const current = property?.merchScenarios;
  if (!current) return fr ? 'Les scénarios de l’actif n’ont pas pu être lus. Réessayez.' : 'The asset’s scenarios could not be read. Retry.';
  const active = w.scenarios.find(s => s.active);
  const item = publishedItem(draft, active?.name ?? draft.name, { id: state.userContext.dbUserId ?? '', name: '' }, {
    valueCreated: w.scenario.yieldPct ? w.totals.valueCreated : null, uplift: w.totals.uplift, works: w.totals.works,
    occupancy: `${w.totals.occupancyScenario.let}/${w.totals.occupancyScenario.total}`,
  }, !!w.property.analystAssumptions?.mockup || w.totals.estimatedRents > 0);
  const items = [...current.items.filter(i => i.id !== item.id), item];
  try {
    await k.mutateInventory([{ entity: 'Property', action: 'UPDATE', id: w.property.id, payload: { merchScenarios: { revision: current.revision, items } } }], 'Publish a Merch scenario to the asset');
    await k.syncEntity('Property', w.property.id, { force: true, requireFresh: true });
  } catch (error) {
    return error instanceof Error ? error.message : (fr ? 'La publication a échoué.' : 'Publishing failed.');
  }
  k.logUserAction('Merch Scenario Published To Asset', { propertyId: w.property.id, planId: draft.id });
  return null;
}
